/**
 * Data Hub payload types and pure helpers.
 *
 * The requests themselves live in `Store/Reducers/DataHubConsoleSlice.ts` and
 * are reached by dispatching its thunks — nothing in this file talks to the
 * server. The run-session handle the wizard drives is in `./runSession.ts`.
 */
import type { DatasetKind } from "./dataset";
import type { PhaseKey } from "./types";

/** One import retained in the object store. */
export type StoredFile = {
  storage_path: string;
  filename: string;
  size_bytes: number;
  uploaded_at: string;
  file_hash: string | null;
};

/* ---------------- Ledger (resumable Import & Run) ---------------- */

export type LedgerAttempt = {
  id?: string;
  step: string;
  attempt_number?: number;
  status: string;
  artifact_evidence?: Record<string, any> | null;
  outcome_counts?: Record<string, any> | null;
  error?: string | null;
  reason?: string | null;
};

export type LedgerStep = {
  step: string;
  status: string;
  group?: string;
  attempts: LedgerAttempt[];
};

export type LedgerRun = {
  id: string;
  organization_id?: string;
  dataset_kind: DatasetKind;
  status: string;
  current_step: string | null;
  source_filename: string | null;
  storage_path: string | null;
  file_hash: string | null;
  sheet_name: string | null;
  row_count: number | null;
  started_at: string | null;
  finished_at: string | null;
  decisions: {
    header_overrides?: unknown;
    column_mapping?: unknown;
    account_decisions?: unknown;
    metric_decisions?: unknown;
    category_decisions?: unknown;
  };
  progress?: {
    phase_1_complete?: boolean;
    next?: string | null;
  };
  steps: LedgerStep[];
};

/** Phase 1 still has work; succeeded and killed runs stay read-only. */
export function isIncompleteLedgerRun(run: {
  status?: string | null;
}): boolean {
  const status = run.status ?? "";
  return status !== "succeeded" && status !== "killed";
}

export type ReportKey = "qc_file" | "qc_production" | "audit";

export const REPORTS: { key: ReportKey; label: string; blurb: string }[] = [
  {
    key: "qc_file",
    label: "QC Report — File",
    blurb: "What QC found in this import, row by row.",
  },
  {
    key: "qc_production",
    label: "QC Report — Production",
    blurb: "What reached production, and how it looks now.",
  },
  {
    key: "audit",
    label: "Audit Report",
    blurb: "Every attempt at every step, with who ran it and how long it took.",
  },
];

/* ---------------- run execution ---------------- */

export type RunPhaseOutcome = {
  phase: PhaseKey;
  status: "success" | "failed" | "skipped";
  count?: number;
  /** What the count is *of*, plus its denominator when the phase knows one. */
  measure?: PhaseMeasure;
  error?: string;
};

/**
 * ApiRequest throws `new Error(JSON.stringify({status, message}))`, so the useful
 * text is nested. Older handling stringified the object and produced
 * "[object Object]" in the run summary.
 */
export function describeError(err: any): string {
  if (!err) return "phase failed";
  const raw = typeof err === "string" ? err : err.message;
  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      const m = parsed?.message ?? parsed?.detail ?? parsed?.response?.message;
      if (typeof m === "string" && m) return m;
      if (m) return JSON.stringify(m);
    } catch {
      if (raw) return raw;
    }
  }
  const direct = err?.response?.message ?? err?.detail ?? err?.message;
  if (typeof direct === "string" && direct) return direct;
  try {
    return JSON.stringify(err);
  } catch {
    return "phase failed";
  }
}

/** One real category, as the server stores it. */
export type CategoryOption = {
  key: string;
  label: string;
  long_label?: string | null;
  /** Other names that mean this category, e.g. "Financial Contract Performance". */
  aliases?: string[];
};

function rowsFrom(payload: any): number | undefined {
  const c = payload?.content ?? payload ?? {};
  const s = c.summary ?? {};
  const n =
    c.rows_scanned ??
    c.buckets_written ??
    c.rows_written ??
    c.updated_rows ??
    c.created_metrics ??
    c.rows ??
    c.count ??
    // importer / staging-transfer shapes
    s.imported_rows ??
    s.inserted_rows ??
    c.transferred_rows ??
    c.processed_rows;
  return typeof n === "number" ? n : undefined;
}

/**
 * What a phase actually counted, and in what unit.
 *
 * Every phase used to report "N rows" whatever it had done, which was wrong for
 * four of the eight: Benchmark produces one row per *metric*, Summarize writes
 * *period rollups*, and both score phases work per *account*. Worse, the
 * generic extraction above knows none of Benchmark's keys, so it reported no
 * number at all — a stage that had processed forty metrics displayed nothing
 * and read as though it had done nothing.
 *
 * `total` is present where the phase knows its denominator, so the console can
 * say "38/40 metrics" rather than a bare count with no sense of scale.
 */
export type PhaseMeasure = {
  count?: number;
  total?: number;
  unit: string;
};

const PHASE_UNITS: Record<string, string> = {
  import: "rows",
  cleanse: "rows",
  qc: "rows",
  commit: "rows",
  promote: "rows",
  benchmark: "metrics",
  normalize: "rows",
  summarize: "period rollups",
  kpi_scores: "accounts",
  ro_scores: "accounts",
};

export function measureFor(phase: string, payload: any): PhaseMeasure {
  const c = payload?.content ?? payload ?? {};
  const unit = PHASE_UNITS[phase] ?? "rows";
  const num = (v: any): number | undefined =>
    typeof v === "number" ? v : undefined;

  switch (phase) {
    case "benchmark": {
      // Benchmark reports per metric, and knows how many it declined to build.
      // Both halves matter: "38 metrics" alone hides that two were skipped for
      // want of data.
      const created = num(c.created_metrics);
      const skipped = num(c.skipped_metrics) ?? 0;
      return {
        count: created,
        total: created == null ? undefined : created + skipped,
        unit,
      };
    }
    case "summarize":
      return { count: num(c.buckets_written), unit };
    case "kpi_scores":
    case "ro_scores":
      return { count: num(c.accounts_scored), unit };
    case "normalize":
      return { count: num(c.normalized_rows) ?? rowsFrom(payload), unit };
    default:
      return { count: rowsFrom(payload), unit };
  }
}

/**
 * "38/40 metrics", or "1,204 rows" when there is no denominator.
 *
 * A denominator of zero is dropped rather than rendered: "0/0" tells the user
 * nothing and reads as a failure, which is the bug the reference logged against
 * its own Normalize stage.
 */
export function formatMeasure(m: PhaseMeasure): string | null {
  if (m.count == null) return null;
  const count = m.count.toLocaleString();
  if (m.total != null && m.total > 0) {
    return `${count}/${m.total.toLocaleString()} ${m.unit}`;
  }
  return `${count} ${m.unit}`;
}

export type RunSession = {
  runId: string;
  /** Set once Import succeeds; Promote needs it. */
  batchId: () => string | number | undefined;
  /**
   * `options.plan` reaches the QC pass only. Omitted, the server uses its
   * default plan — which is what every phase but `cleanse` wants, and what
   * `cleanse` wants until someone opens the probe toggles.
   */
  runPhase: (
    phase: PhaseKey,
    options?: { plan?: Record<string, boolean> | null }
  ) => Promise<RunPhaseOutcome>;
  /**
   * Record a step that made a decision rather than executing work — Columns,
   * Accounts, Metrics, Categories. Without this the run history says nothing
   * about what a human actually decided.
   */
  recordStep: (
    step: string,
    detail: { count?: number | null; extras?: Record<string, unknown> }
  ) => Promise<string | null>;
  /** The server rolls the status up from the recorded phases; no argument. */
  close: () => Promise<void>;
};

/* ---------------- Custom rubric (Phase 5) ---------------- */

export type CustomMetric = {
  metric_id: string;
  metric_name: string | null;
  category_key: string | null;
  subcategory?: string | null;
  unit_of_measure?: string | null;
  format?: string | null;
  scale?: string | null;
  normalization_type?: string | null;
  normalization_direction?: string | null;
  description?: string | null;
  business_impact?: string | null;
  formula?: string | null;
  source_system?: string | null;
  kpi_status?: string | null;
  enabled?: boolean | null;
};

export type RubricCategory = {
  category_key: string;
  label: string | null;
  long_label?: string | null;
  description?: string | null;
  source: string;
  enabled: boolean;
  custom_metric_count: number;
};

export type RubricImportPlanEntry = {
  metric_id: string;
  metric_name: string;
  action: "create" | "update" | "unchanged" | "standard" | "error";
  reason?: string;
  category_key?: string | null;
  changes?: string[];
};

/** One duplicate group whose rows disagree on the value. */
export type QcConflictGroup = {
  key: string;
  account_name: string | null;
  metric_id: string | null;
  metric_name: string | null;
  measurement_date: string | null;
  candidates: {
    row_id: string;
    value: string | null;
    source_system: string | null;
    imported_at: string | null;
  }[];
};

/** Narrows a bulk Heal action to one problem type and/or one metric. */
export type HealScope = { probeId?: string | null; metricId?: string | null };

/* ---------------- QA audit ---------------- */

export type QaCheckResult = {
  id: string;
  label: string;
  group: string;
  status: "pass" | "warn" | "fail" | "error" | "skipped";
  count: number | null;
  detail: string;
};

export type QaReport = {
  summary: {
    total: number;
    passed: number;
    warnings: number;
    failures: number;
    errors: number;
    skipped: number;
  };
  checks: QaCheckResult[];
};

/* ---------------- QC summary for the wizard's QC step ---------------- */

export type QcRunSummary = {
  rows_scanned: number;
  buckets: {
    clean: number;
    fixed: number;
    open: number;
    rejected: number;
    ignored?: number;
    duplicate?: number;
    conflict?: number;
    user_rejected?: number;
  };
  /** Rows with no verdict yet, e.g. a fix accepted since the last QC pass. */
  unscanned?: number;
  by_probe: {
    probe_id: string;
    label: string;
    group: string | null;
    severity: string | null;
    count: number;
  }[];
  dedupe: { auto_deduped: number; conflicts: number };
};
